import { Badge, Box, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core'
import { IconRouteAltLeft } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { EstadoDeConsulta } from '@/components/ui'
import { acoesGatesQuery, type AcaoGate } from '@/api/roles'

/**
 * Quem decide esta acao, em um selo -- derivado de `quem_decide`, NAO do tamanho de
 * `funcoes_exigidas`.
 *
 * A diferenca nao e' estilistica. `chamado_comment` sai por `chamado_write` e tem
 * `funcoes_exigidas` VAZIO, entao ler o tamanho da lista o rotularia "todos os
 * usuarios" -- e ele exige a permissao de chamados no ambiente DO CHAMADO. O selo
 * afirmaria acesso que nao existe, contradizendo o proprio motivo escrito ao lado.
 */
function SeloDeQuemDecide({ acao }: { acao: AcaoGate }) {
  if (acao.quem_decide === 'todos') {
    return <Badge size="xs" variant="light" color="teal" tt="none">todos os usuários</Badge>
  }
  if (acao.quem_decide === 'permissao_de_chamados') {
    return (
      <Badge size="xs" variant="light" color="inovareTeal" tt="none">
        permissão de chamados
      </Badge>
    )
  }
  return (
    <>
      {acao.funcoes_exigidas.map((f) => (
        <Badge key={f} size="xs" variant="light" color="red" ff="monospace" tt="none">
          só {f}
        </Badge>
      ))}
    </>
  )
}

/**
 * Diz, na tela de Permissoes, o que conceder a permissao NAO resolve.
 *
 * Existe porque conceder a permissao e' o gesto visivel, e em parte das acoes do chat ele
 * nao basta: a funcao e' cobrada num portao anterior (Gate 1) e a permissao no seguinte
 * (Gate 2). Quem concede `instance:restart` a alguem sem a funcao ve o pedido ser negado
 * e nao tem, nesta tela, nada que explique por que.
 *
 * Vive em arquivo proprio porque `Permissoes.tsx` passou de 1400 linhas e e' campo de
 * conflito em repo multiagente -- e porque isto muda quando o codigo do chat muda, num
 * ritmo que nao e' o do editor de templates.
 *
 * O conteudo e' derivado no servidor (`role_gates.gate_efetivo_das_acoes`), respeitando os
 * tres desvios que vem ANTES do portao de funcao. Ler `allowed_roles` e parar ali afirma
 * coisa errada: `ack_alert` e `chamado_comment` tem o campo preenchido e NAO passam pelo
 * portao.
 */
export function PermissoesComGateDeFuncao() {
  const consulta = useQuery(acoesGatesQuery)

  const exigemFuncao = Object.entries(consulta.data?.permissoes_que_exigem_funcao ?? {})
  const semPermissao = (consulta.data?.acoes ?? []).filter((a) => a.permissao === null)

  return (
    <Card radius="md" withBorder p={0} style={{ borderColor: 'var(--ai-border-light)' }}>
      <Box
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--ai-border-light)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <IconRouteAltLeft size={16} style={{ color: 'var(--ai-brand-verde)' }} />
        <Text fw={700} size="sm" c="var(--ai-text-primary)">
          O que conceder a permissão não resolve
        </Text>
      </Box>

      <Box p="md">
        <EstadoDeConsulta
          consulta={consulta}
          vazio={exigemFuncao.length === 0 && semPermissao.length === 0}
          estadoVazio={
            <Text size="xs" c="var(--ai-text-secondary)">
              Nenhuma ação do chat exige função além da permissão. Conceder a permissão
              basta em todas elas.
            </Text>
          }
        >
          <Stack gap="md">
            {exigemFuncao.length > 0 && (
              <Stack gap={6}>
                <Text size="xs" c="var(--ai-text-primary)">
                  Estas permissões são cobradas <b>depois</b> de um portão de função. Sem a
                  função, a ação é negada mesmo com a permissão concedida — e sem a
                  permissão, ter a função também não basta.
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={8}>
                  {exigemFuncao.map(([permissao, funcoes]) => (
                    <Box
                      key={permissao}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 4,
                        border: '1px solid var(--ai-border-light)',
                        backgroundColor: 'var(--ai-body-bg)',
                      }}
                    >
                      <Text size="xs" fw={700} ff="monospace" c="var(--ai-text-primary)">
                        {permissao}
                      </Text>
                      <Group gap={4} mt={4}>
                        <Text size="xs" c="var(--ai-text-muted)">exige também a função</Text>
                        {funcoes.map((f) => (
                          <Badge key={f} size="xs" variant="light" color="inovareTeal" ff="monospace" tt="none">
                            {f}
                          </Badge>
                        ))}
                      </Group>
                    </Box>
                  ))}
                </SimpleGrid>
              </Stack>
            )}

            {semPermissao.length > 0 && (
              <Stack gap={6}>
                <Text size="xs" fw={700} c="var(--ai-text-primary)">
                  Ações do chat sem permissão a conceder ({semPermissao.length})
                </Text>
                <Text size="xs" c="var(--ai-text-secondary)">
                  Para estas não existe permissão nesta tela. O que decide cada uma está
                  ao lado — e não é a mesma coisa em todas.
                </Text>
                <Stack gap={6}>
                  {semPermissao.map((a) => (
                    <Box
                      key={a.acao}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 4,
                        border: '1px solid var(--ai-border-light)',
                        backgroundColor: 'var(--ai-body-bg)',
                      }}
                    >
                      <Group gap={8} wrap="nowrap" align="center">
                        <Text size="xs" fw={600} c="var(--ai-text-primary)" style={{ flex: 1 }}>
                          {a.label}
                        </Text>
                        <SeloDeQuemDecide acao={a} />
                      </Group>
                      <Text size="xs" c="var(--ai-text-primary)" mt={2}>{a.motivo}</Text>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </EstadoDeConsulta>
      </Box>
    </Card>
  )
}
